import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type { FeatureListOptions } from "@shared-core/data/adapter";
import { FeatureCard } from "../app/components/domain/feature-sessions/FeatureCard";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../app/components/primitives/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Label } from "../app/components/primitives/label";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { getDefaultRepository } from "@shared-core/data/feature-session-types";
import type {
  FeatureBranch,
  SessionKind,
} from "@shared-core/data/feature-session-types";
import { paths } from "@shared-core/routing/routes";
import { useCanManageFeatureSessions } from "../app/auth/hooks";

// FeatureSessionsPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md) with one documented deviation: scope is
// per-user (owner), not per-portfolio. Filter / sort / search run
// server-side; the page refetches on change.

type StatusFilter = "all" | "active" | "idle";
type SortOption = "updated" | "created" | "name";

const SORT_OPTION_TO_PARAM: Record<SortOption, string> = {
  updated: "-updated_at",
  created: "-created_at",
  name: "branch_name",
};

const PAGE_SIZE = 100;

export function FeatureSessionsPage() {
  const dataAdapter = useDataAdapter();
  const navigate = useNavigate();

  const [features, setFeatures] = useState<FeatureBranch[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("updated");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [newSessionKind, setNewSessionKind] = useState<SessionKind>("chat");
  const [featureToDelete, setFeatureToDelete] = useState<FeatureBranch | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const canUseRemoteControl = useCanManageFeatureSessions();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const options: FeatureListOptions = {
        sort: SORT_OPTION_TO_PARAM[sortBy],
        pageSize: PAGE_SIZE,
      };
      if (search.trim()) options.q = search.trim();
      if (statusFilter !== "all") options.sessionStatus = statusFilter;
      try {
        const result = await dataAdapter.getFeatureSessions(options);
        if (!cancelled) setFeatures(result.items);
      } catch (error) {
        console.error("Error loading features:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, search, statusFilter, sortBy]);

  const filtered = features;

  async function handleCreate() {
    if (!newBranchName.trim()) return;
    setCreating(true);
    try {
      const feature = await dataAdapter.createFeature({
        branch_name: newBranchName.trim(),
        repository: getDefaultRepository(),
        // Feature sessions are a personal (owner-scoped) workspace; visibility
        // is always owner-only, so there is no visibility choice to make.
        visibility_scope: "owner",
        session_kind: newSessionKind,
      });
      // Refetch to pick up the new feature with current filters.
      const refreshed = await dataAdapter.getFeatureSessions({
        sort: SORT_OPTION_TO_PARAM[sortBy],
        pageSize: PAGE_SIZE,
      });
      setFeatures(refreshed.items);
      setCreateOpen(false);
      setNewBranchName("");
      setNewSessionKind("chat");
      toast.success(`Feature branch created: ${feature.branch_name}`);
      navigate(
        `${paths.featureSessionWorkspace({ branchName: feature.branch_name })}?repo=${encodeURIComponent(feature.repository)}`,
      );
    } catch (error) {
      console.error("Error creating feature:", error);
      if (error instanceof ApiError) {
        if (error.status >= 400 && error.status < 500 && error.detail) {
          // Surface the server's own message for client errors (validation,
          // conflicts, etc.) — it's specific and actionable.
          toast.error(error.detail);
        } else {
          toast.error(
            "Something went wrong creating the feature. Please try again.",
          );
        }
      } else {
        toast.error(
          "Could not connect to the server. Please check your connection.",
        );
      }
    } finally {
      setCreating(false);
    }
  }

  function handleCardClick(feature: FeatureBranch) {
    navigate(
      `${paths.featureSessionWorkspace({ branchName: feature.branch_name })}?repo=${encodeURIComponent(feature.repository)}`,
    );
  }

  async function handleDelete() {
    if (!featureToDelete) return;
    setDeleting(true);
    try {
      await dataAdapter.deleteFeature(
        featureToDelete.repository,
        featureToDelete.branch_name,
      );
      setFeatures((prev) =>
        prev.filter(
          (f) =>
            !(
              f.repository === featureToDelete.repository &&
              f.branch_name === featureToDelete.branch_name
            ),
        ),
      );
      toast.success(`Deleted ${featureToDelete.branch_name}`);
      setFeatureToDelete(null);
    } catch (error) {
      console.error("Error deleting feature:", error);
      toast.error("Failed to delete feature");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        Loading features...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Features</h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4 mr-2" />
          New Feature
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search branches..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="idle">Idle</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={sortBy}
          onValueChange={(v) => setSortBy(v as SortOption)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated">Last updated</SelectItem>
            <SelectItem value="created">Date created</SelectItem>
            <SelectItem value="name">Branch name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {features.length === 0
            ? "No features yet. Create one to get started."
            : "No features match your filters."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((feature) => (
            <FeatureCard
              key={`${feature.repository}/${feature.branch_name}`}
              feature={feature}
              onClick={() => handleCardClick(feature)}
              onDelete={() => setFeatureToDelete(feature)}
            />
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Feature Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="branch-name">Branch name</Label>
              <Input
                id="branch-name"
                placeholder="feature/my-feature"
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !creating) handleCreate();
                }}
              />
            </div>
            {canUseRemoteControl && (
              <div className="space-y-2">
                <Label htmlFor="session-kind">Session type</Label>
                <Select
                  value={newSessionKind}
                  onValueChange={(v) => setNewSessionKind(v as SessionKind)}
                >
                  <SelectTrigger id="session-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chat">Chat (guided)</SelectItem>
                    <SelectItem value="remote_control">
                      Remote control (drive from claude.ai/code)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newBranchName.trim()}
            >
              {creating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={featureToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setFeatureToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {featureToDelete?.branch_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This stops any running session and removes the feature from your
              list. The GitHub branch is kept for 30 days, then cleaned up —
              your work stays recoverable until then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
